import { useEffect, useMemo, useState } from 'react';

import { ChakraFlex, SkeletonLoader } from '@equip.health/ui';
import { DateTime } from 'luxon';
import { debounce } from 'lodash';
import ScheduleChangerV2, {
  GroupsListTypeOptions,
} from '~/components/common/calendar/schedule/ScheduleChangerV2';
import GroupClassWeekView from '../group-class/GroupClassWeekView';
import { useGetGroupClassCountsV2Query } from '~/store/services/groupClass';
import RenderGroupsList from './groupsList';
import RenderSectionsList from './sectionsList';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { GroupSectionEvents } from '~/analytics/groupSectionEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

const TOP_LEVEL_ID = 'groups-wrapper';

export type GroupsTabProps = {
  count?: number;
  listType: GroupsListTypeOptions;
  tabCount?: number;
  title: string;
};

export enum GroupsTabTitle {
  ALL_GROUPS = 'All groups',
  MY_SECTIONS = 'My sections',
  WEEKLY_VIEW = 'Weekly view',
  ARCHIVE = 'Archive',
}

const GroupsWrapper = () => {
  const [currentDate, setCurrentDate] = useState<DateTime>(DateTime.now());
  const [groupClassTitle, setGroupClassTitle] = useState<string>('');
  const [debouncedGroupClassTitle, setDebouncedGroupClassTitle] =
    useState<string>('');
  const [listType, setListType] = useState<GroupClassListType>(
    GroupsListTypeOptions.ALL_GROUPS,
  );

  const [isNewGroupCreated, setIsNewGroupCreated] = useState<boolean>(false);

  const { data: groupCountsData } = useGetGroupClassCountsV2Query();
  const { trackEvent } = useAnalytics();

  const handleSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedGroupClassTitle(value.trim());
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      handleSearchChange.cancel?.();
    };
  }, [handleSearchChange]);

  useEffect(() => {
    const viewedEventMap = {
      [GroupsListTypeOptions.ALL_GROUPS]: GroupSectionEvents.allGroupsTabViewed,
      [GroupsListTypeOptions.MY_SECTIONS]:
        GroupSectionEvents.mySectionsTabViewed,
      [GroupsListTypeOptions.WEEKLY]: GroupSectionEvents.weeklyViewTabViewed,
      [GroupsListTypeOptions.ARCHIVE]: GroupSectionEvents.archiveTabViewed,
    };

    const eventToTrack = viewedEventMap[listType];
    if (eventToTrack && trackEvent) {
      trackEvent(eventToTrack, {
        Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
      });
    }
  }, [listType, trackEvent]);

  const onSearchInputChange = (value: string) => {
    setGroupClassTitle(value);

    if (value.trim() === '') {
      handleSearchChange.cancel();
      setDebouncedGroupClassTitle('');
    } else {
      handleSearchChange(value);
    }
  };

  const scheduleTabs: Record<GroupsListTypeOptions, GroupsTabProps> = useMemo(
    () => ({
      [GroupsListTypeOptions.ALL_GROUPS]: {
        count: groupCountsData?.activeGroupsCount ?? 0,
        tabCount: groupCountsData?.activeGroupsCount ?? 0,
        listType: GroupsListTypeOptions.ALL_GROUPS,
        title: GroupsTabTitle.ALL_GROUPS,
      },
      [GroupsListTypeOptions.MY_SECTIONS]: {
        count: groupCountsData?.mySectionsCount ?? 0,
        tabCount: groupCountsData?.mySectionsCount ?? 0,
        listType: GroupsListTypeOptions.MY_SECTIONS,
        title: GroupsTabTitle.MY_SECTIONS,
      },
      [GroupsListTypeOptions.WEEKLY]: {
        listType: GroupsListTypeOptions.WEEKLY,
        title: GroupsTabTitle.WEEKLY_VIEW,
      },
      [GroupsListTypeOptions.ARCHIVE]: {
        count: groupCountsData?.archivedGroupsCount ?? 0,
        listType: GroupsListTypeOptions.ARCHIVE,
        title: GroupsTabTitle.ARCHIVE,
      },
    }),
    [groupCountsData],
  );

  const renderGroupClasses = () => {
    switch (listType) {
      case GroupsListTypeOptions.WEEKLY:
        return <GroupClassWeekView currentDate={currentDate} />;
      case GroupsListTypeOptions.ARCHIVE:
      case GroupsListTypeOptions.ALL_GROUPS:
        return (
          <RenderGroupsList
            listType={listType}
            groupClassTitle={debouncedGroupClassTitle}
            totalCount={
              listType === GroupsListTypeOptions.ALL_GROUPS
                ? (groupCountsData?.activeGroupsCount ?? 0)
                : (groupCountsData?.archivedGroupsCount ?? 0)
            }
            isNewGroupCreated={isNewGroupCreated}
            resetNewGroupCreated={() => setIsNewGroupCreated(false)}
          />
        );
      case GroupsListTypeOptions.MY_SECTIONS:
        return (
          <RenderSectionsList
            groupClassTitle={debouncedGroupClassTitle}
            totalCount={groupCountsData?.mySectionsCount ?? 0}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ChakraFlex
      direction="column"
      height="100%"
      id={TOP_LEVEL_ID}
      overflow="hidden"
      paddingRight="0px"
    >
      {scheduleTabs ? (
        <>
          <ScheduleChangerV2
            currentDate={currentDate}
            groupClassTitle={groupClassTitle}
            onCreateClass={() => setIsNewGroupCreated(true)}
            onDateChange={(direction) => {
              setCurrentDate((prev) =>
                direction === 'next'
                  ? prev.plus({ weeks: 1 })
                  : prev.minus({ weeks: 1 }),
              );
            }}
            onTabChanged={setListType}
            setGroupClassTitle={onSearchInputChange}
            tabsV2={scheduleTabs}
          />
          {renderGroupClasses()}
        </>
      ) : (
        <SkeletonLoader
          id={`${TOP_LEVEL_ID}__loader`}
          isLoaded={scheduleTabs !== undefined}
        />
      )}
    </ChakraFlex>
  );
};

export default GroupsWrapper;
